import { useTranslation } from "react-i18next";
import { AiOutlineEye, AiOutlineExpand } from "react-icons/ai";
import { RxCross2 } from "react-icons/rx";
import { FileTypeTile, attachmentDetails, extensionOf } from "./ChatFileCard";
import styles from "./StagedAttachment.module.scss";

// Types the in-page AttachmentPreview can show from a local copy.
const PREVIEWABLE_EXTENSIONS = ["pdf", "csv", "txt"];
const PREVIEWABLE_TYPES = ["application/pdf", "text/csv", "text/plain"];

/**
 * The file picked in a chat, shown above the message box until it is sent (same design as the
 * admin panel's chat): thumbnail or type tile, name, "PDF · 236 KB · Ready to send", plus
 * Preview (opens the in-page AttachmentPreview with the local copy) and Remove.
 *
 * Props:
 *  - file (File), isImage (bool), previewUrl (local object URL of `file`, or null)
 *  - onPreview(attachment): receives { url, kind, fileName, contentType, fileSize }, the same
 *    shape the chats keep for AttachmentPreview
 *  - onRemove()
 */
const StagedAttachment = ({ file, isImage = false, previewUrl, onPreview, onRemove }) => {
  const { t } = useTranslation();
  if (!file) return null;

  const name = file.name || t("chat.preview.attachment", "Attachment");
  const contentType = file.type || "";
  const details = attachmentDetails({ fileName: name, contentType, fileSize: file.size, isImage });
  const canPreview =
    !!previewUrl &&
    !!onPreview &&
    (isImage ||
      PREVIEWABLE_TYPES.includes(contentType.toLowerCase()) ||
      PREVIEWABLE_EXTENSIONS.includes(extensionOf(name)));
  const openPreview = () =>
    onPreview?.({
      url: previewUrl,
      kind: isImage ? "image" : "file",
      fileName: name,
      contentType,
      fileSize: file.size,
    });

  const previewLabel = t("chat.preview.preview", "Preview");
  const readyLabel = t("chat.preview.readyToSend", "Ready to send");

  return (
    <div className={styles.wrap}>
      <div className={styles.card} role="group" aria-label={`${readyLabel}: ${name}`}>
        {isImage && previewUrl ? (
          <button
            type="button"
            className={styles.thumb}
            onClick={canPreview ? openPreview : undefined}
            aria-label={`${previewLabel} ${name}`}
            title={t("chat.preview.clickToView", "Click to view")}
          >
            <img src={previewUrl} alt="" />
            <span className={styles.thumbOverlay} aria-hidden="true">
              <AiOutlineExpand size={16} />
            </span>
          </button>
        ) : (
          <FileTypeTile fileName={name} contentType={contentType} isImage={isImage} />
        )}

        <div className={styles.body}>
          <p className={styles.name} title={name}>
            {name}
          </p>
          <p className={styles.details}>
            {details}
            {details ? " · " : ""}
            <span className={styles.ready}>{readyLabel}</span>
          </p>
        </div>

        {canPreview && (
          <button
            type="button"
            className={`${styles.action} ${styles.previewBtn}`}
            onClick={openPreview}
            aria-label={`${previewLabel} ${name}`}
          >
            <AiOutlineEye size={16} aria-hidden="true" />
            <span className={styles.previewLabel}>{previewLabel}</span>
          </button>
        )}
        <button
          type="button"
          className={`${styles.action} ${styles.removeBtn}`}
          onClick={onRemove}
          aria-label={`${t("chat.preview.remove", "Remove")} ${name}`}
          title={t("chat.preview.remove", "Remove")}
        >
          <RxCross2 size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default StagedAttachment;
